"use client"

import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"
import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * OKS-616: Base UI's Tooltip (unlike Radix) wires NO trigger↔popup association
 * — it registers no `useRole` interaction, so it emits neither `role="tooltip"`
 * nor `aria-describedby`. This context restores that association at the
 * primitive level (parity with Radix, which did it automatically): the `Tooltip`
 * root mints a stable id + tracks open state; `TooltipContent` stamps the id on
 * the Popup; `TooltipTrigger` points `aria-describedby` at it while open. Fixes
 * SR announcement for all consumers at once — notably `crew-stat-tile`'s
 * empty-value "—" glyph, whose sole SR path is this describedby (no aria-label
 * by design).
 */
const TooltipContext = React.createContext<{
  contentId: string
  open: boolean
} | null>(null)

/**
 * Tooltip provider. Preserves the shadcn `delayDuration` consumer API
 * (Radix parity) by mapping it onto Base UI's `delay` prop, so existing
 * call sites (`delayDuration={0 | 120 | 300}`) keep working unchanged.
 */
function TooltipProvider({
  delayDuration = 0,
  ...props
}: Omit<TooltipPrimitive.Provider.Props, "delay"> & {
  delayDuration?: number
}) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  open: openProp,
  onOpenChange,
  children,
  ...props
}: TooltipPrimitive.Root.Props) {
  const contentId = React.useId()
  const [internalOpen, setInternalOpen] = React.useState(false)
  const open = openProp ?? internalOpen
  return (
    <TooltipProvider>
      <TooltipContext.Provider value={{ contentId, open }}>
        <TooltipPrimitive.Root
          data-slot="tooltip"
          open={openProp}
          onOpenChange={(nextOpen, eventDetails) => {
            setInternalOpen(nextOpen)
            onOpenChange?.(nextOpen, eventDetails)
          }}
          {...props}
        >
          {children}
        </TooltipPrimitive.Root>
      </TooltipContext.Provider>
    </TooltipProvider>
  )
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  const ctx = React.useContext(TooltipContext)
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      aria-describedby={ctx?.open ? ctx.contentId : undefined}
      {...props}
    />
  )
}

/**
 * Tooltip content. `side` / `sideOffset` position the Base UI `Positioner`
 * (Radix positioned these on `Content`); remaining props flow to the `Popup`.
 * Open/close animations key on Base UI's `data-open` / `data-closed`
 * attributes and the `--transform-origin` var (Radix used `data-state` +
 * `--radix-tooltip-content-transform-origin`).
 */
function TooltipContent({
  className,
  sideOffset = 0,
  side,
  align,
  children,
  ...props
}: TooltipPrimitive.Popup.Props & {
  sideOffset?: TooltipPrimitive.Positioner.Props["sideOffset"]
  side?: TooltipPrimitive.Positioner.Props["side"]
  align?: TooltipPrimitive.Positioner.Props["align"]
}) {
  const ctx = React.useContext(TooltipContext)
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        sideOffset={sideOffset}
        side={side}
        align={align}
      >
        <TooltipPrimitive.Popup
          // OKS-616: Base UI's tooltip (unlike Radix) registers no `useRole`
          // interaction, so it emits no `role="tooltip"` and no id link. Set the
          // role + id explicitly to preserve the WAI-ARIA tooltip semantic +
          // `aria-describedby` parity with the Radix version. (elementProps win
          // over Base UI's popupProps.)
          role="tooltip"
          id={ctx?.contentId}
          data-slot="tooltip-content"
          className={cn(
            "bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
            className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
